import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, Pencil, Trash2, Shield } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [property, setProperty] = useState({});
  const [saving, setSaving] = useState(false);
  const [whitelist, setWhitelist] = useState([]);
  const [ipModal, setIpModal] = useState(false);
  const [editingIp, setEditingIp] = useState(null);
  const [ipForm, setIpForm] = useState({ cidr: '', label: '' });

  const fetchSettings = useCallback(async () => {
    const res = await api.get("/admin/settings");
    setSettings(res.data.settings || {});
    setProperty(res.data.property || {});
  }, []);

  const fetchWhitelist = useCallback(async () => {
    try {
      const res = await api.get("/admin/ip-whitelist");
      setWhitelist(res.data);
    } catch (err) { toast.error("Failed to load IP whitelist"); }
  }, []);

  useEffect(() => { fetchSettings(); fetchWhitelist(); }, [fetchSettings, fetchWhitelist]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { settings, property });
      toast.success("Settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const openIpModal = (entry = null) => {
    setEditingIp(entry);
    setIpForm(entry ? { cidr: entry.cidr, label: entry.label || '' } : { cidr: '', label: '' });
    setIpModal(true);
  };

  const saveIp = async () => {
    try {
      if (editingIp) {
        await api.put(`/admin/ip-whitelist/${editingIp.id}`, ipForm);
      } else {
        await api.post("/admin/ip-whitelist", ipForm);
      }
      toast.success(editingIp ? "IP updated" : "IP added");
      setIpModal(false);
      fetchWhitelist();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save IP"); }
  };

  const deleteIp = async (entry) => {
    if (!window.confirm(`Remove ${entry.cidr} (${entry.label})? If this is your current IP, you will be locked out.`)) return;
    try {
      await api.delete(`/admin/ip-whitelist/${entry.id}`);
      toast.success("IP removed");
      fetchWhitelist();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight">Settings</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Settings */}
        <div className="bg-white border border-gray-200 p-6 space-y-4">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400 mb-4">System Settings</h2>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Session Timeout (minutes)</Label>
            <Input type="number" value={settings.session_timeout_minutes || "30"}
              onChange={(e) => setSettings({ ...settings, session_timeout_minutes: e.target.value })}
              className="mt-1 rounded-none" data-testid="setting-timeout" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Max PIN Attempts</Label>
            <Input type="number" value={settings.max_pin_attempts || "5"}
              onChange={(e) => setSettings({ ...settings, max_pin_attempts: e.target.value })}
              className="mt-1 rounded-none" data-testid="setting-max-pin" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Auto Gratuity %</Label>
            <Input type="number" step="0.5" value={settings.auto_gratuity_percent || "20"}
              onChange={(e) => setSettings({ ...settings, auto_gratuity_percent: e.target.value })}
              className="mt-1 rounded-none" data-testid="setting-gratuity" />
            <p className="text-[10px] text-gray-400 mt-1">Set to 0 to disable. Cashier can remove per transaction.</p>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Room Number Format (Regex)</Label>
            <Input value={settings.room_number_format_regex || ""}
              onChange={(e) => setSettings({ ...settings, room_number_format_regex: e.target.value })}
              className="mt-1 rounded-none font-mono text-sm" data-testid="setting-room-regex" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">OHIP Cashier ID</Label>
            <Input type="number" value={settings.ohip_cashier_id || "1"}
              onChange={(e) => setSettings({ ...settings, ohip_cashier_id: e.target.value })}
              className="mt-1 rounded-none" data-testid="setting-ohip-cashier" />
            <p className="text-[10px] text-gray-400 mt-1">Cashier ID assigned to this POS in Opera Cloud. Required for charge posting.</p>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">OHIP Gratuity TRX Code</Label>
            <Input value={settings.ohip_gratuity_trx_code || "7522"}
              onChange={(e) => setSettings({ ...settings, ohip_gratuity_trx_code: e.target.value })}
              className="mt-1 rounded-none font-mono text-sm" data-testid="setting-ohip-gratuity-code" />
            <p className="text-[10px] text-gray-400 mt-1">Opera transaction code for posting gratuity (e.g. 7522 = Mini Bar Gratuity).</p>
          </div>
        </div>

        {/* Property Settings */}
        <div className="bg-white border border-gray-200 p-6 space-y-4">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400 mb-4">Property</h2>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Property Name</Label>
            <Input value={property.property_name || ""}
              onChange={(e) => setProperty({ ...property, property_name: e.target.value })}
              className="mt-1 rounded-none" data-testid="setting-prop-name" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Property Code</Label>
            <Input value={property.property_code || ""}
              onChange={(e) => setProperty({ ...property, property_code: e.target.value })}
              className="mt-1 rounded-none" data-testid="setting-prop-code" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Currency Code</Label>
            <Input value={property.currency_code || ""}
              onChange={(e) => setProperty({ ...property, currency_code: e.target.value })}
              className="mt-1 rounded-none" data-testid="setting-currency" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Revenue Center Code</Label>
            <Input value={property.revenue_center_code || ""}
              onChange={(e) => setProperty({ ...property, revenue_center_code: e.target.value })}
              className="mt-1 rounded-none" />
          </div>
        </div>

        {/* IP Whitelist */}
        <div className="bg-white border border-gray-200 p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-red-500" />
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400">IP Access Whitelist</h2>
            </div>
            <Button onClick={() => openIpModal()} className="rounded-none bg-black text-white hover:bg-gray-800 text-xs h-8" data-testid="add-ip-button">
              <Plus className="w-3 h-3 mr-1" /> Add IP
            </Button>
          </div>
          <p className="text-xs text-gray-500 mb-3">Only listed IP addresses and CIDR ranges can access this application. Removing all entries disables the whitelist (allows all).</p>

          {whitelist.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
              No IP restrictions configured. All IPs can access the application.
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead><tr className="border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-500">
                <th className="text-left py-2 px-2">IP / CIDR</th>
                <th className="text-left">Label</th>
                <th className="text-left">Added</th>
                <th className="text-right">Actions</th>
              </tr></thead>
              <tbody>
                {whitelist.map(entry => (
                  <tr key={entry.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2 px-2 font-mono font-medium">{entry.cidr}</td>
                    <td className="text-gray-600">{entry.label || '—'}</td>
                    <td className="text-gray-400">{entry.created_at?.slice(0, 10)}</td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => openIpModal(entry)}
                          data-testid={`edit-ip-${entry.id}`}><Pencil className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-red-400" onClick={() => deleteIp(entry)}
                          data-testid={`delete-ip-${entry.id}`}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="rounded-none bg-black text-white hover:bg-gray-800 h-10 px-8 text-sm" data-testid="save-settings-button">
        {saving ? "Saving..." : "Save Settings"}
      </Button>

      {/* IP Modal */}
      <Dialog open={ipModal} onOpenChange={setIpModal}>
        <DialogContent className="rounded-none border-gray-200 max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{editingIp ? "Edit" : "Add"} IP Address</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">IP Address or CIDR Range</Label>
              <Input value={ipForm.cidr} onChange={(e) => setIpForm({ ...ipForm, cidr: e.target.value })}
                placeholder="e.g. 192.168.1.0/24 or 10.0.0.1" className="mt-1 rounded-none font-mono" data-testid="ip-cidr-input" />
              <p className="text-[10px] text-gray-400 mt-1">Single IP (e.g. 10.0.0.1) or CIDR range (e.g. 10.0.0.0/24)</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Label (optional)</Label>
              <Input value={ipForm.label} onChange={(e) => setIpForm({ ...ipForm, label: e.target.value })}
                placeholder="e.g. Hotel Front Desk" className="mt-1 rounded-none" data-testid="ip-label-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIpModal(false)} className="rounded-none">Cancel</Button>
            <Button onClick={saveIp} disabled={!ipForm.cidr} className="rounded-none bg-black text-white hover:bg-gray-800" data-testid="save-ip-button">
              {editingIp ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
